import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, type AutocompleteOption } from "@/components/ui/autocomplete";
import { Label } from "@/components/ui/label";
import {
  DEFAULT_DOCUMENT_TYPE_NAMES,
  normalizeDocumentTypeName,
} from "@/lib/pet-documents/document-upload";

interface DocumentTypeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export function DocumentTypeSelector({ value, onChange, disabled }: DocumentTypeSelectorProps) {
  const { t } = useTranslation("medicalRecords");

  const options = useMemo<AutocompleteOption[]>(
    () => DEFAULT_DOCUMENT_TYPE_NAMES.map((type) => ({ label: type, value: type })),
    [],
  );

  const trimmedValue = value.trim();
  const normalizedValue = trimmedValue ? normalizeDocumentTypeName(trimmedValue) : "";
  const selectedValue = options.some((option) => option.value === normalizedValue) ? normalizedValue : "";

  return (
    <div className="space-y-2">
      <Label>{t("documentTypes.fieldLabel")} <span className="text-destructive">*</span></Label>
      <Autocomplete
        options={options}
        value={value}
        onChange={onChange}
        onSelect={(option) => onChange(option.value)}
        selectedValue={selectedValue}
        placeholder={t("documentTypes.placeholder")}
        emptyMessage={t("documentTypes.empty")}
        disabled={disabled}
        popoverPortalled={false}
        preventParentScrollOnFocus
      />
    </div>
  );
}
